// modals/sales/EditSale.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import { Trash2, Save, Search, RotateCcw, Clock } from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleActions, saleProductActions } from '../../action/saleActions';
import { customerActions } from '../../action/customerActions';

const EditSale = ({ saleId, onSuccess, onClose, isOpen = false }) => {
    const modalRef = useRef(null);

    // Rounding function
    const roundAmount = (amount) => {
        return Math.round(amount);
    };

    const [formData, setFormData] = useState({
        id: '',
        invoice_no: '',
        sale_date: dayjs().format('YYYY-MM-DD'),
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        subtotal: 0,
        tax: 0,
        discount: 0,
        spl_disc_percent: 0,
        spl_disc_amount: 0,
        shipping: 0,
        additional_charge: 0,
        adjustment: 0,
        grand_total: 0,
        rounded_grand_total: 0,
        round_off: 0,
        enable_rounding: true,
        paid_amount: 0,
        due_amount: 0,
        payment_status: 'unpaid',
        payment_mode: '',
        po_number: '',
        dc_number: '',
        remarks: '',
        status: 'pending',
        items: [],
        payment_history: [],
        sgst: 0,
        cgst: 0,
        igst: 0,
        created_by: 'Admin'
    });

    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [outOfStockMessage, setOutOfStockMessage] = useState('');
    const [error, setError] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [additionalPayment, setAdditionalPayment] = useState(0);
    const [newPaymentMode, setNewPaymentMode] = useState('');
    // Raw string values for numeric inputs (allows typing 0.005, clears 0 on focus)
    const [rawInputValues, setRawInputValues] = useState({});

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'processing', label: 'Processing' },
        { value: 'shipped', label: 'Shipped' },
        { value: 'delivered', label: 'Delivered' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Payment mode options
    const paymentModeOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'card', label: 'Card' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'upi', label: 'UPI' },
        { value: 'cheque', label: 'Cheque' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'partial', label: 'Partial' },
        { value: 'paid', label: 'Paid' },
        { value: 'advance_payment', label: 'Advance Payment' },
        { value: 'against_payment', label: 'Against Payment' },
        { value: 'pay_in_15', label: 'Pay in 15 Days' },
        { value: 'pay_in_30', label: 'Pay in 30 Days' },
        { value: 'pay_in_45', label: 'Pay in 45 Days' }
    ];

    // Credit-term statuses preserved when paid amount is 0
    const CREDIT_TERM_STATUSES = ['pay_in_15', 'pay_in_30', 'pay_in_45'];

    // Clean up modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('edit-sale');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('edit-sale');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    // Load sale data including payment history
    const loadSaleData = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await saleActions.getSale(saleId);

            if (result.success) {
                const sale = result.data.sale || result.data;
                const items = result.data.items || [];

                // Parse payment history - handle both array and JSON string
                let paymentHistory = [];
                if (sale.payment_history) {
                    if (Array.isArray(sale.payment_history)) {
                        paymentHistory = sale.payment_history;
                    } else if (typeof sale.payment_history === 'string') {
                        try {
                            paymentHistory = JSON.parse(sale.payment_history) || [];
                        } catch (e) {
                            console.error('Error parsing payment history:', e);
                            paymentHistory = [];
                        }
                    }
                }

                // Also check for payments array from API
                if (result.data.payments && Array.isArray(result.data.payments)) {
                    paymentHistory = result.data.payments;
                }

                // Normalise payment history field names (backend payments table uses
                // payment_date/payment_method; frontend-created entries use date/method)
                paymentHistory = (Array.isArray(paymentHistory) ? paymentHistory : []).map(p => ({
                    ...p,
                    date: p.date || p.payment_date || null,
                    method: p.method || p.payment_method || p.mode || '',
                    amount: parseFloat(p.amount || 0),
                    notes: p.notes || ''
                }));

                // Fallback payment mode: if the sale has no stored payment_mode, use the
                // method from the most recent payment history entry so the dropdown shows it.
                let resolvedPaymentMode = sale.payment_mode || '';
                if (!resolvedPaymentMode && paymentHistory.length > 0) {
                    const lastWithMethod = [...paymentHistory].reverse().find(p => p.method);
                    if (lastWithMethod) resolvedPaymentMode = lastWithMethod.method;
                }

                // Calculate totals from items to ensure they're correct
                let totalSubtotal = 0;
                let totalSGST = 0;
                let totalCGST = 0;
                let totalIGST = 0;
                let totalTax = 0;

                // Format the data with proper tax splits
                const formattedItems = items.map(item => {
                    const taxPercent = parseFloat(item.tax_percent || 0);
                    const taxAmount = parseFloat(item.tax_amount || 0);
                    const quantity = parseFloat(item.quantity || 0);
                    const unitPrice = parseFloat(item.unit_price || 0);
                    const discountPercent = parseFloat(item.discount || 0);

                    // Calculate discount amount if not provided
                    const subtotal = unitPrice * quantity;
                    const discountAmount = parseFloat(item.discount_amount) || ((subtotal * discountPercent) / 100);
                    const taxableAmount = subtotal - discountAmount;

                    // Calculate tax amounts if they're not provided
                    const calculatedTaxAmount = taxAmount || (taxableAmount * taxPercent / 100);
                    const calculatedSGST = calculatedTaxAmount / 2;
                    const calculatedCGST = calculatedTaxAmount / 2;

                    // Accumulate totals
                    totalSubtotal += subtotal;
                    totalSGST += calculatedSGST;
                    totalCGST += calculatedCGST;
                    totalTax += calculatedTaxAmount;

                    return {
                        id: item.id,
                        product_id: item.product_id,
                        product_code: item.product_code || item.sku,
                        product_name: item.product_name,
                        hsn_code: item.hsn_code || '',
                        sku: item.sku,
                        units: item.units || 'PCS',
                        quantity: quantity,
                        unit_price: unitPrice,
                        discount: discountPercent,
                        discount_amount: discountAmount,
                        taxable_amount: taxableAmount,
                        tax_percent: taxPercent,
                        tax_amount: calculatedTaxAmount,
                        sgst_percent: taxPercent / 2,
                        cgst_percent: taxPercent / 2,
                        sgst_amount: calculatedSGST,
                        cgst_amount: calculatedCGST,
                        igst_amount: 0,
                        unit_cost: parseFloat(item.unit_cost || 0),
                        total_cost: taxableAmount + calculatedTaxAmount,
                        category_id: item.category_id,
                        brand_id: item.brand_id
                    };
                });

                // Format the data
                const formattedData = {
                    id: sale.id,
                    invoice_no: sale.invoice_no,
                    sale_date: sale.sale_date || dayjs().format('YYYY-MM-DD'),
                    customer_id: sale.customer_id,
                    customer_name: sale.customer_name,
                    customer_phone: sale.customer_phone,
                    subtotal: parseFloat(sale.subtotal || totalSubtotal),
                    tax: parseFloat(sale.tax || totalTax),
                    discount: parseFloat(sale.discount || 0),
                    spl_disc_percent: parseFloat(sale.spl_disc_percent || 0),
                    spl_disc_amount: parseFloat(sale.spl_disc_amount || 0),
                    // courier_charge from addSales = shipping in edit; also read sale.shipping as fallback
                    shipping: parseFloat(sale.courier_charge || sale.shipping || 0),
                    additional_charge: parseFloat(sale.additional_charge || 0),
                    adjustment: parseFloat(sale.adjustment || 0),
                    grand_total: parseFloat(sale.grand_total || 0),
                    rounded_grand_total: parseFloat(sale.rounded_grand_total || sale.grand_total || 0),
                    round_off: parseFloat(sale.round_off || 0),
                    enable_rounding: sale.enable_rounding !== undefined ? sale.enable_rounding : true,
                    paid_amount: parseFloat(sale.paid_amount || 0),
                    due_amount: parseFloat(sale.due_amount || 0),
                    payment_status: sale.payment_status || 'unpaid',
                    payment_mode: resolvedPaymentMode,
                    po_number: sale.po_number || '',
                    dc_number: sale.dc_number || '',
                    remarks: sale.notes || sale.remarks || '',
                    status: sale.status || 'pending',
                    payment_history: paymentHistory,
                    items: formattedItems,
                    sgst: totalSGST,
                    cgst: totalCGST,
                    igst: totalIGST,
                    created_by: sale.created_by || 'Admin'
                };

                setFormData(formattedData);
                setOriginalData(formattedData);

                // Set customer
                if (sale.customer_id) {
                    setSelectedCustomer({
                        value: sale.customer_id,
                        label: sale.customer_name,
                        phone: sale.customer_phone
                    });
                }

                // Set date
                if (sale.sale_date) {
                    setSelectedDate(dayjs(sale.sale_date));
                }

                // Show modal after data is loaded
                setTimeout(() => showBootstrapModal(), 100);

            } else {
                setError(result.message || 'Failed to load sale data');
                Swal.fire('Error', result.message || 'Failed to load sale data', 'error');
            }
        } catch (error) {
            console.error('Error loading sale:', error);
            setError('Failed to load sale data');
            Swal.fire('Error', 'Failed to load sale data', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Load customers
    const loadCustomers = async () => {
        try {
            const result = await customerActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: '', label: 'Select Customer' },
                    ...result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} (${c.phone || 'No phone'})`,
                        phone: c.phone,
                        email: c.email,
                        address: c.address,
                        ...c
                    }))
                ]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    // Initialize
    useEffect(() => {
        if (saleId) {
            loadSaleData();
            loadCustomers();
        }

        return () => {
            cleanupModalBackdrops();
        };
    }, [saleId]);

    // Handle isOpen prop
    useEffect(() => {
        if (isOpen && !loading && formData.id) {
            setTimeout(() => showBootstrapModal(), 100);
        }
    }, [isOpen, loading, formData.id]);

    // Clear raw display values when item rows change (add/remove)
    useEffect(() => {
        setRawInputValues({});
    }, [formData.items.length]);

    // Calculate totals with rounding
    const calculateTotals = (items, shipping = 0, paidAmount = 0, enableRounding = true, splDiscPercent = 0, additionalCharge = 0, currentStatus = null) => {
        const subtotal = items.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0);
        const totalDiscount = items.reduce((sum, i) => sum + (i.discount_amount || 0), 0);

        // Calculate tax amounts from items
        const totalTax = items.reduce((sum, i) => sum + (i.tax_amount || 0), 0);
        const totalSGST = items.reduce((sum, i) => sum + (i.sgst_amount || 0), 0);
        const totalCGST = items.reduce((sum, i) => sum + (i.cgst_amount || 0), 0);
        const totalIGST = items.reduce((sum, i) => sum + (i.igst_amount || 0), 0);

        // If SGST and CGST are zero but tax amount exists, split it
        let finalSGST = totalSGST;
        let finalCGST = totalCGST;

        if (totalSGST === 0 && totalCGST === 0 && totalTax > 0) {
            finalSGST = totalTax / 2;
            finalCGST = totalTax / 2;
        }

        // Spl Disc applied on ex-tax base (subtotal - item discounts)
        const exdTaxTotal = subtotal - totalDiscount;
        const splDiscAmount = (exdTaxTotal * parseFloat(splDiscPercent || 0)) / 100;
        const afterSplDisc = exdTaxTotal - splDiscAmount;

        const grandTotal = afterSplDisc + totalTax + Number(shipping) + Number(additionalCharge);

        // Calculate rounded values
        let roundedGrandTotal = grandTotal;
        let roundOff = 0;

        if (enableRounding) {
            roundedGrandTotal = roundAmount(grandTotal);
            roundOff = roundedGrandTotal - grandTotal;
        }

        const finalGrandTotal = enableRounding ? roundedGrandTotal : grandTotal;
        const dueAmount = finalGrandTotal - paidAmount;

        let paymentStatus = 'unpaid';
        if (paidAmount >= finalGrandTotal && finalGrandTotal > 0) paymentStatus = 'paid';
        else if (paidAmount > 0) paymentStatus = 'partial';
        else if (currentStatus && ['pay_in_15', 'pay_in_30', 'pay_in_45'].includes(currentStatus)) {
            paymentStatus = currentStatus; // preserve credit-term when nothing paid
        }

        return {
            subtotal: parseFloat(subtotal.toFixed(2)),
            discount: parseFloat(totalDiscount.toFixed(2)),
            spl_disc_amount: parseFloat(splDiscAmount.toFixed(2)),
            tax: parseFloat(totalTax.toFixed(2)),
            sgst: parseFloat(finalSGST.toFixed(2)),
            cgst: parseFloat(finalCGST.toFixed(2)),
            igst: parseFloat(totalIGST.toFixed(2)),
            grand_total: parseFloat(grandTotal.toFixed(2)),
            rounded_grand_total: parseFloat(roundedGrandTotal.toFixed(2)),
            round_off: parseFloat(roundOff.toFixed(2)),
            grand_total_display: parseFloat(finalGrandTotal.toFixed(2)),
            due_amount: parseFloat(dueAmount.toFixed(2)),
            payment_status: paymentStatus
        };
    };

    // Safe function to calculate total paid from payment history
    const calculateTotalPaidFromHistory = () => {
        if (!formData.payment_history) return 0;

        // Ensure payment_history is an array
        const history = Array.isArray(formData.payment_history)
            ? formData.payment_history
            : [];

        return history.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    };

    // Toggle rounding
    // Handle special discount percent change  — disabled in EditSale (spl_disc comes from addSalesPage)
    // const handleSplDiscPercentChange = (value) => {
    //     const percent = parseFloat(value) || 0;
    //     setFormData(prev => {
    //         const totals = calculateTotals(prev.items, prev.shipping, prev.paid_amount, prev.enable_rounding, percent, prev.additional_charge);
    //         return { ...prev, spl_disc_percent: percent, ...totals };
    //     });
    // };

    // Handle additional charge change
    const handleAdditionalChargeChange = (value) => {
        const charge = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(prev.items, prev.shipping, prev.paid_amount, prev.enable_rounding, prev.spl_disc_percent, charge);
            return { ...prev, additional_charge: charge, ...totals };
        });
    };

    const handleToggleRounding = () => {
        setFormData(prev => {
            const newEnableRounding = !prev.enable_rounding;
            const totals = calculateTotals(
                prev.items,
                prev.shipping,
                prev.paid_amount,
                newEnableRounding,
                prev.spl_disc_percent,
                prev.additional_charge
            );

            return {
                ...prev,
                enable_rounding: newEnableRounding,
                ...totals
            };
        });
    };

    // Handle quantity change
    const handleQuantityChange = (index, value) => {
        const qty = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const actualQty = Math.max(0, qty);

            const subtotal = item.unit_price * actualQty;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                quantity: actualQty,
                discount_amount: discountAmount,
                taxable_amount: taxableAmount,
                tax_amount: taxAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(updatedItems, prev.shipping, prev.paid_amount, prev.enable_rounding, prev.spl_disc_percent);

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle price change
    const handlePriceChange = (index, value) => {
        const price = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = price * item.quantity;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                unit_price: price,
                discount_amount: discountAmount,
                taxable_amount: taxableAmount,
                tax_amount: taxAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(updatedItems, prev.shipping, prev.paid_amount, prev.enable_rounding, prev.spl_disc_percent);

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle tax change
    const handleTaxChange = (index, value) => {
        const taxPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const taxableAmount = item.unit_price * item.quantity - (item.discount_amount || 0);
            const taxAmount = (taxableAmount * taxPercent) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                tax_percent: taxPercent,
                tax_amount: taxAmount,
                sgst_percent: taxPercent / 2,
                cgst_percent: taxPercent / 2,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(updatedItems, prev.shipping, prev.paid_amount, prev.enable_rounding, prev.spl_disc_percent);

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle discount change
    const handleDiscountChange = (index, value) => {
        const discountPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = item.unit_price * item.quantity;
            const discountAmount = (subtotal * discountPercent) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                discount: discountPercent,
                discount_amount: discountAmount,
                taxable_amount: taxableAmount,
                tax_amount: taxAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(updatedItems, prev.shipping, prev.paid_amount, prev.enable_rounding, prev.spl_disc_percent);

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Remove item
    const handleRemoveItem = (index) => {
        Swal.fire({
            title: 'Remove Item?',
            text: 'Are you sure you want to remove this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData(prev => {
                    const updatedItems = prev.items.filter((_, i) => i !== index);
                    const totals = calculateTotals(updatedItems, prev.shipping, prev.paid_amount, prev.enable_rounding);

                    return {
                        ...prev,
                        items: updatedItems,
                        ...totals
                    };
                });

                Swal.fire('Removed!', 'Item removed.', 'success');
            }
        });
    };

    // Search products
    const handleProductSearch = async (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setSearching(true);

        if (term.length >= 2) {
            const result = await saleProductActions.searchProducts(term);
            if (result.success) {
                const allResults = result.data || [];
                // Only show products that have stock available
                const inStockResults = allResults.filter(
                    p => parseFloat(p.quantity || 0) > 0
                );
                setSearchResults(inStockResults);
                // If matches exist but all are out of stock, notify the user
                if (inStockResults.length === 0 && allResults.length > 0) {
                    setOutOfStockMessage('Matching product(s) found, but stock is 0. Cannot add out-of-stock items.');
                } else {
                    setOutOfStockMessage('');
                }
                setShowProductSearch(true);
            }
        } else {
            setSearchResults([]);
            setOutOfStockMessage('');
            setShowProductSearch(false);
        }
        setSearching(false);
    };

    // Add product
    const handleAddProduct = (product) => {
        // Block adding products that are out of stock
        if (parseFloat(product.quantity || 0) <= 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Out of Stock',
                text: `${product.product_name} is out of stock and cannot be added.`
            });
            return;
        }

        // Search results expose the id as `value`; fall back to `id`/`product_id`.
        const productId = product.id ?? product.value ?? product.product_id;
        const existingItem = formData.items.find(i => i.product_id === productId);

        if (existingItem) {
            handleQuantityChange(
                formData.items.indexOf(existingItem),
                existingItem.quantity + 1
            );
            setSearchTerm('');
            setShowProductSearch(false);
            return;
        }

        const unitPrice = parseFloat(product.selling_price || 0);
        const taxPercent = parseFloat(product.tax_rate || 0);
        const sgstPercent = taxPercent / 2;
        const cgstPercent = taxPercent / 2;

        const subtotal = unitPrice * 1;
        const discountAmount = 0;
        const taxableAmount = subtotal - discountAmount;
        const taxAmount = (taxableAmount * taxPercent) / 100;
        const sgstAmount = taxAmount / 2;
        const cgstAmount = taxAmount / 2;
        const totalCost = taxableAmount + taxAmount;

        const newItem = {
            id: `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
            product_id: productId,
            product_code: product.sku,
            product_name: product.product_name,
            hsn_code: product.hsn_code || '',
            sku: product.sku,
            units: 'PCS',
            quantity: 1,
            unit_price: unitPrice,
            discount: 0,
            discount_amount: 0,
            taxable_amount: taxableAmount,
            tax_percent: taxPercent,
            tax_amount: taxAmount,
            sgst_percent: sgstPercent,
            cgst_percent: cgstPercent,
            sgst_amount: sgstAmount,
            cgst_amount: cgstAmount,
            igst_amount: 0,
            unit_cost: parseFloat(product.cost_price || 0),
            total_cost: totalCost,
            category_id: product.category_id,
            brand_id: product.brand_id
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals(updatedItems, prev.shipping, prev.paid_amount, prev.enable_rounding);

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });

        setSearchTerm('');
        setShowProductSearch(false);
    };

    // Handle customer change
    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setFormData(prev => ({
            ...prev,
            customer_id: option?.value || '',
            customer_name: option?.label || '',
            customer_phone: option?.phone || ''
        }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setFormData(prev => ({ ...prev, sale_date: date.format('YYYY-MM-DD') }));
        }
    };

    // Handle paid amount change
    const handlePaidAmountChange = (value) => {
        const paidAmount = parseFloat(value) || 0;
        const maxAmount = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;
        const validPaidAmount = Math.min(paidAmount, maxAmount);

        setFormData(prev => {
            const totals = calculateTotals(prev.items, prev.shipping, validPaidAmount, prev.enable_rounding, prev.spl_disc_percent, prev.additional_charge || 0, prev.payment_status);
            return {
                ...prev,
                paid_amount: validPaidAmount,
                ...totals
            };
        });
    };

    // Quick action to set due amount to zero (full payment) - WITH PAYMENT HISTORY
    const handleFullPayment = () => {
        const maxAmount = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;
        const additionalAmount = maxAmount - formData.paid_amount;

        if (additionalAmount <= 0) {
            Swal.fire('Info', 'Sale is already fully paid', 'info');
            return;
        }

        // Ask for payment mode
        Swal.fire({
            title: 'Full Payment',
            html: `
                <div class="text-start">
                    <p>Amount to pay: <strong>${formatCurrency(additionalAmount)}</strong></p>
                    <p>Current paid: ${formatCurrency(formData.paid_amount)}</p>
                    <p>New total: ${formatCurrency(maxAmount)}</p>
                    <div class="mb-3">
                        <label class="form-label">Payment Mode</label>
                        <select id="payment-mode" class="form-select">
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="upi">UPI</option>
                            <option value="cheque">Cheque</option>
                        </select>
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Confirm Payment',
            confirmButtonColor: '#28a745',
            preConfirm: () => {
                const mode = document.getElementById('payment-mode').value;
                return mode;
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const paymentMode = result.value;

                // Create new payment record
                const newPayment = {
                    id: `temp_payment_${Date.now()}`,
                    date: dayjs().format('YYYY-MM-DD'),
                    amount: additionalAmount,
                    method: paymentMode,
                    reference: null,
                    notes: 'Full payment during edit',
                    status: 'completed'
                };

                setFormData(prev => {
                    const totals = calculateTotals(prev.items, prev.shipping, maxAmount, prev.enable_rounding);

                    // Ensure payment_history is an array before spreading
                    const currentHistory = Array.isArray(prev.payment_history) ? prev.payment_history : [];

                    // Add to payment history
                    const updatedHistory = [...currentHistory, newPayment];

                    return {
                        ...prev,
                        paid_amount: maxAmount,
                        payment_mode: paymentMode,
                        payment_history: updatedHistory,
                        ...totals
                    };
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Payment Added',
                    text: `Payment of ${formatCurrency(additionalAmount)} recorded`,
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        });
    };

    // Handle additional payment modal
    const handleAdditionalPaymentSubmit = () => {
        if (additionalPayment <= 0) {
            Swal.fire('Error', 'Please enter a valid amount', 'error');
            return;
        }

        if (!newPaymentMode) {
            Swal.fire('Error', 'Please select a payment mode', 'error');
            return;
        }

        const currentPaid = formData.paid_amount;
        const newPaid = currentPaid + additionalPayment;
        const maxAmount = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

        if (newPaid > maxAmount) {
            Swal.fire('Error', `Total paid cannot exceed ${formatCurrency(maxAmount)}`, 'error');
            return;
        }

        // Create new payment record
        const newPayment = {
            id: `temp_payment_${Date.now()}`,
            date: dayjs().format('YYYY-MM-DD'),
            amount: additionalPayment,
            method: newPaymentMode,
            reference: null,
            notes: 'Additional payment during edit',
            status: 'completed'
        };

        setFormData(prev => {
            const totals = calculateTotals(prev.items, prev.shipping, newPaid, prev.enable_rounding);

            // Ensure payment_history is an array before spreading
            const currentHistory = Array.isArray(prev.payment_history) ? prev.payment_history : [];

            // Add to payment history
            const updatedHistory = [...currentHistory, newPayment];

            return {
                ...prev,
                paid_amount: newPaid,
                payment_mode: newPaymentMode,
                payment_history: updatedHistory,
                ...totals
            };
        });

        setShowPaymentModal(false);

        Swal.fire({
            icon: 'success',
            title: 'Payment Added',
            text: `Additional payment of ${formatCurrency(additionalPayment)} recorded`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    // Handle shipping change
    const handleShippingChange = (value) => {
        const shipping = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(prev.items, shipping, prev.paid_amount, prev.enable_rounding, prev.spl_disc_percent, prev.additional_charge);
            return {
                ...prev,
                shipping,
                ...totals
            };
        });
    };

    // Handle status change
    const handleStatusChange = (option) => {
        setFormData(prev => ({ ...prev, status: option?.value || 'pending' }));
    };

    // Handle payment mode change
    const handlePaymentModeChange = (option) => {
        setFormData(prev => ({
            ...prev,
            payment_mode: option?.value || ''
        }));
    };

    // Handle payment status change — auto-updates paid/due amounts based on selected status
    const handlePaymentStatusChange = (option) => {
        const status = option?.value || 'unpaid';
        setFormData(prev => {
            const grandTotal = prev.enable_rounding ? prev.rounded_grand_total : prev.grand_total;
            let paidAmount = prev.paid_amount;
            let dueAmount = prev.due_amount;

            if (status === 'advance_payment' || status === 'against_payment' || status === 'paid') {
                paidAmount = grandTotal;
                dueAmount = 0;
            } else if (status === 'unpaid' || CREDIT_TERM_STATUSES.includes(status)) {
                paidAmount = 0;
                dueAmount = grandTotal;
            }
            // 'partial' → keep existing paid amount; user enters manually

            return { ...prev, payment_status: status, paid_amount: paidAmount, due_amount: dueAmount };
        });
    };

    // Validate form
    const validateForm = () => {
        if (!formData.customer_id) {
            Swal.fire('Error', 'Please select a customer', 'error');
            return false;
        }

        if (formData.items.length === 0) {
            Swal.fire('Error', 'Please add at least one item', 'error');
            return false;
        }

        // Payment status consistency check
        const grandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;
        const status = formData.payment_status;

        if (status === 'partial') {
            if (formData.paid_amount <= 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Invalid Paid Amount',
                    text: 'Please enter a paid amount greater than ₹0 for Partial payment.'
                });
                return false;
            }
            if (formData.paid_amount >= grandTotal) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Invalid Payment Status',
                    text: 'Paid amount equals or exceeds the Grand Total. Please select "Paid" status instead.'
                });
                return false;
            }
        }

        if ((status === 'advance_payment' || status === 'against_payment') && formData.paid_amount < grandTotal) {
            Swal.fire({
                icon: 'warning',
                title: 'Payment Mismatch',
                text: `"${status === 'advance_payment' ? 'Advance Payment' : 'Against Payment'}" requires the full amount to be paid. Paid: ₹${formData.paid_amount}, Due: ₹${formData.due_amount}`
            });
            return false;
        }

        return true;
    };

    // Check if data changed
    const hasChanges = () => {
        if (!originalData) return false;

        // Check basic fields
        if (formData.customer_id !== originalData.customer_id) return true;
        if (formData.sale_date !== originalData.sale_date) return true;
        if (formData.status !== originalData.status) return true;
        if (formData.payment_status !== originalData.payment_status) return true;
        if (formData.payment_mode !== originalData.payment_mode) return true;
        if (formData.po_number !== originalData.po_number) return true;
        if (formData.dc_number !== originalData.dc_number) return true;
        if (formData.remarks !== originalData.remarks) return true;
        if (formData.shipping !== originalData.shipping) return true;
        if (formData.spl_disc_percent !== originalData.spl_disc_percent) return true;
        if (formData.paid_amount !== originalData.paid_amount) return true;
        if (formData.enable_rounding !== originalData.enable_rounding) return true;

        // Check payment history length
        const currentHistory = Array.isArray(formData.payment_history) ? formData.payment_history : [];
        const originalHistory = Array.isArray(originalData.payment_history) ? originalData.payment_history : [];

        if (currentHistory.length !== originalHistory.length) return true;

        // Check items
        if (formData.items.length !== originalData.items.length) return true;

        for (let i = 0; i < formData.items.length; i++) {
            const newItem = formData.items[i];
            const oldItem = originalData.items[i];

            if (!oldItem) return true;

            if (newItem.product_id !== oldItem.product_id) return true;
            if (newItem.quantity !== oldItem.quantity) return true;
            if (newItem.unit_price !== oldItem.unit_price) return true;
            if (newItem.discount !== oldItem.discount) return true;
            if (newItem.tax_percent !== oldItem.tax_percent) return true;
        }

        return false;
    };

    // Handle submit - INCLUDES PAYMENT HISTORY
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        if (!hasChanges()) {
            Swal.fire('Info', 'No changes to save', 'info');
            return;
        }

        try {
            setSaving(true);

            // Remove temporary IDs from new items
            const itemsToSend = formData.items.map(item => {
                const { id, ...rest } = item;
                if (id && !id.toString().startsWith('temp_')) {
                    return { ...rest, id };
                }
                return rest;
            });

            // Prepare payment history - filter out temporary IDs
            const currentHistory = Array.isArray(formData.payment_history) ? formData.payment_history : [];
            const paymentHistoryToSend = currentHistory.map(payment => {
                const { id, ...rest } = payment;
                if (id && !id.toString().startsWith('temp_payment_')) {
                    return { ...rest, id };
                }
                return rest;
            });

            // Use the appropriate grand total based on rounding
            const finalGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

            const dataToSend = {
                ...formData,
                items: itemsToSend,
                payment_history: paymentHistoryToSend,
                grand_total: finalGrandTotal,
                rounded_grand_total: formData.rounded_grand_total,
                round_off: formData.round_off,
                enable_rounding: formData.enable_rounding
            };

            console.log('Submitting with payment history:', dataToSend.payment_history);

            const result = await saleActions.updateSale(saleId, dataToSend);

            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Sale updated successfully',
                    timer: 2000
                });

                hideBootstrapModal();
                if (onSuccess) onSuccess();
            } else {
                Swal.fire('Error', result.message || 'Failed to update sale', 'error');
            }
        } catch (error) {
            console.error('Error updating sale:', error);
            Swal.fire('Error', 'Failed to update sale', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (hasChanges()) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    hideBootstrapModal();
                    if (onClose) onClose();
                }
            });
        } else {
            hideBootstrapModal();
            if (onClose) onClose();
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    // ── Numeric input helpers (raw-string pattern) ──
    const getRaw = (key, numericVal) =>
        key in rawInputValues ? rawInputValues[key] : String(numericVal ?? 0);

    const onNumFocus = (key, numericVal) => {
        if (parseFloat(numericVal) === 0) {
            setRawInputValues(prev => ({ ...prev, [key]: '' }));
        }
    };

    const onNumBlur = (key) => {
        const raw = rawInputValues[key];
        if (raw === '' || raw === undefined) {
            setRawInputValues(prev => { const c = { ...prev }; delete c[key]; return c; });
        }
    };

    const onNumChange = (key, value, numericSetter) => {
        setRawInputValues(prev => ({ ...prev, [key]: value }));
        numericSetter(value);
    };

    // Format date
    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    // Calculate summary stats
    const totalItems = formData.items.length;
    const totalQty = formData.items.reduce((sum, i) => sum + (i.quantity || 0), 0);

    // Get current grand total for display
    const currentGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

    // Calculate total paid from payment history
    const totalPaidFromHistory = calculateTotalPaidFromHistory();

    // If still loading, show loading overlay
    if (loading) {
        return (
            <div className="modal fade" id="edit-sale" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Sale</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading sale data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If error, show error modal
    if (error || !formData.id) {
        return (
            <div className="modal fade" id="edit-sale" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title text-danger">Error</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <p>{error || 'Failed to load sale data'}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="modal fade" id="edit-sale" ref={modalRef} tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Sale - {formData.invoice_no}</h5>
                            <button type="button" className="btn-close" onClick={handleCancel}></button>
                        </div>

                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                {/* Invoice Number and Date */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Invoice Number</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.invoice_no}
                                            readOnly
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Date</label>
                                        <DatePicker
                                            value={selectedDate}
                                            onChange={handleDateChange}
                                            format="DD/MM/YYYY"
                                            className="datetimepicker"
                                            style={{ width: '100%' }}
                                            allowClear={false}
                                            getPopupContainer={trigger => trigger.parentElement}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">PO Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.po_number}
                                            onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                                            placeholder="Enter PO number"
                                        />
                                    </div>
                                </div>

                                {/* Customer Section */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Customer Name *</label>
                                        <Select
                                            options={customers}
                                            value={selectedCustomer}
                                            onChange={handleCustomerChange}
                                            placeholder="Select Customer"
                                            isClearable
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Phone</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light"
                                            value={formData.customer_phone}
                                            readOnly
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">DC Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.dc_number}
                                            onChange={(e) => setFormData(prev => ({ ...prev, dc_number: e.target.value }))}
                                            placeholder="Enter DC number"
                                        />
                                    </div>
                                </div>

                                {/* Product Search */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Add Product</label>
                                    <div className="position-relative">
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <Search size={16} className="text-muted" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search product by name or code..."
                                                value={searchTerm}
                                                onChange={handleProductSearch}
                                            />
                                            {searching && (
                                                <span className="input-group-text bg-white">
                                                    <div className="spinner-border spinner-border-sm text-primary" />
                                                </span>
                                            )}
                                        </div>

                                        {showProductSearch && searchResults.length === 0 && outOfStockMessage && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm p-2" style={{ zIndex: 1000 }}>
                                                <small className="text-danger d-flex align-items-center">
                                                    <i className="feather icon-alert-triangle me-1" />
                                                    {outOfStockMessage}
                                                </small>
                                            </div>
                                        )}

                                        {showProductSearch && searchResults.length > 0 && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                                {searchResults.map(product => (
                                                    <div
                                                        key={product.id}
                                                        className="p-2 border-bottom hover-bg-light"
                                                        onClick={() => handleAddProduct(product)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <strong>{product.product_name}</strong>
                                                                <br />
                                                                <small className="text-muted">Code: {product.sku}</small>
                                                            </div>
                                                            <div className="text-end">
                                                                <span className="text-primary">{formatCurrency(product.selling_price)}</span>
                                                                <br />
                                                                <small className="text-muted">Stock: {product.quantity || 0}</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Items Table with Tax Columns */}
                                {formData.items.length > 0 && (
                                    <div className="mb-4">
                                        <div className="table-responsive border rounded">
                                            <table className="table table-bordered mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Product Code</th>
                                                        <th>Product Name</th>
                                                        <th>HSN Code</th>
                                                        <th>Units</th>
                                                        <th className="text-end">Qty</th>
                                                        <th className="text-end">Rate</th>
                                                        <th className="text-end">Disc %</th>
                                                        <th className="text-end">SGST %</th>
                                                        <th className="text-end">CGST %</th>
                                                        <th className="text-end">IGST %</th>
                                                        <th className="text-end">Amount</th>
                                                        <th className="text-center">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.items.map((item, index) => (
                                                        <tr key={item.id ? `${item.id}_${index}` : `row_${index}`}>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm bg-light"
                                                                    value={item.product_code || ''}
                                                                    readOnly
                                                                />
                                                            </td>
                                                            <td>
                                                                <strong>{item.product_name}</strong>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm bg-light"
                                                                    value={item.hsn_code || ''}
                                                                    readOnly
                                                                    style={{ width: '80px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={item.units || 'PCS'}
                                                                    onChange={(e) => {
                                                                        const updated = [...formData.items];
                                                                        updated[index].units = e.target.value;
                                                                        setFormData(prev => ({ ...prev, items: updated }));
                                                                    }}
                                                                    style={{ minWidth: '90px' }}
                                                                >
                                                                    {item.units && !['PCS', 'BOX', 'KG', 'LTR'].includes(item.units) && (
                                                                        <option value={item.units}>{item.units}</option>
                                                                    )}
                                                                    <option value="PCS">PCS</option>
                                                                    <option value="BOX">BOX</option>
                                                                    <option value="KG">KG</option>
                                                                    <option value="LTR">LTR</option>
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    value={getRaw(`qty_${index}`, item.quantity)}
                                                                    onChange={(e) => onNumChange(`qty_${index}`, e.target.value, (v) => handleQuantityChange(index, v))}
                                                                    onFocus={() => onNumFocus(`qty_${index}`, item.quantity)}
                                                                    onBlur={() => onNumBlur(`qty_${index}`)}
                                                                    min="0"
                                                                    step="any"
                                                                    style={{ width: '80px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    value={getRaw(`price_${index}`, item.unit_price)}
                                                                    onChange={(e) => onNumChange(`price_${index}`, e.target.value, (v) => handlePriceChange(index, v))}
                                                                    onFocus={() => onNumFocus(`price_${index}`, item.unit_price)}
                                                                    onBlur={() => onNumBlur(`price_${index}`)}
                                                                    min="0"
                                                                    step="any"
                                                                    style={{ width: '100px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    value={getRaw(`disc_${index}`, item.discount || 0)}
                                                                    onChange={(e) => onNumChange(`disc_${index}`, e.target.value, (v) => handleDiscountChange(index, v))}
                                                                    onFocus={() => onNumFocus(`disc_${index}`, item.discount || 0)}
                                                                    onBlur={() => onNumBlur(`disc_${index}`)}
                                                                    min="0"
                                                                    step="any"
                                                                    style={{ width: '70px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    value={getRaw(`sgst_${index}`, item.sgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                                    onChange={(e) => {
                                                                        onNumChange(`sgst_${index}`, e.target.value, (v) => {
                                                                            const sgst = parseFloat(v) || 0;
                                                                            handleTaxChange(index, sgst * 2);
                                                                        });
                                                                    }}
                                                                    onFocus={() => onNumFocus(`sgst_${index}`, item.sgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                                    onBlur={() => onNumBlur(`sgst_${index}`)}
                                                                    min="0"
                                                                    step="any"
                                                                    style={{ width: '70px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    value={getRaw(`cgst_${index}`, item.cgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                                    onChange={(e) => {
                                                                        onNumChange(`cgst_${index}`, e.target.value, (v) => {
                                                                            const cgst = parseFloat(v) || 0;
                                                                            handleTaxChange(index, cgst * 2);
                                                                        });
                                                                    }}
                                                                    onFocus={() => onNumFocus(`cgst_${index}`, item.cgst_percent || (item.tax_percent ? item.tax_percent / 2 : 0))}
                                                                    onBlur={() => onNumBlur(`cgst_${index}`)}
                                                                    min="0"
                                                                    step="any"
                                                                    style={{ width: '70px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm text-end"
                                                                    value="0"
                                                                    readOnly
                                                                    style={{ width: '70px', backgroundColor: '#f8f9fa' }}
                                                                />
                                                            </td>
                                                            <td className="text-end fw-bold">
                                                                {formatCurrency(item.total_cost)}
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleRemoveItem(index)}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Summary Cards */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-2">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">No of Items</span>
                                                    <strong className="text-primary">{totalItems}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">Total Qty</span>
                                                    <strong className="text-info">{totalQty}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">Subtotal</span>
                                                    <strong className="text-success">{formatCurrency(formData.subtotal)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">SGST</span>
                                                    <strong className="text-warning">{formatCurrency(formData.sgst || 0)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">CGST</span>
                                                    <strong className="text-danger">{formatCurrency(formData.cgst || 0)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">IGST</span>
                                                    <strong className="text-secondary">{formatCurrency(formData.igst || 0)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Spl Disc % — disabled, set during sale creation only */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Spl Disc %</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control bg-light"
                                                value={formData.spl_disc_percent || 0}
                                                readOnly
                                                disabled
                                                title="Special discount is set during sale creation and cannot be changed here"
                                            />
                                            <span className="input-group-text">%</span>
                                        </div>
                                        {formData.spl_disc_amount > 0 && (
                                            <small className="text-danger">
                                                Discount: - {formatCurrency(formData.spl_disc_amount)}
                                            </small>
                                        )}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Adjustment (+/-)</label>
                                        <input
                                            type="number"
                                            className="form-control bg-light"
                                            value={formData.adjustment || 0}
                                            readOnly
                                            disabled
                                            title="Adjustment is set during sale creation and cannot be changed here"
                                        />
                                        {formData.adjustment !== 0 && (
                                            <small className={formData.adjustment > 0 ? 'text-success' : 'text-danger'}>
                                                {formData.adjustment > 0 ? `+ ${formatCurrency(formData.adjustment)}` : `- ${formatCurrency(Math.abs(formData.adjustment))}`}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                {/* Grand Total and Discount */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-8">
                                        <div className="card bg-primary text-white">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h5 className="mb-0 text-white">Grand Total</h5>
                                                        {formData.enable_rounding && formData.round_off !== 0 && (
                                                            <small className="text-white-50">
                                                                Actual: {formatCurrency(formData.grand_total)}
                                                            </small>
                                                        )}
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm ${formData.enable_rounding ? 'btn-light' : 'btn-outline-light'} me-2`}
                                                            onClick={handleToggleRounding}
                                                            title={formData.enable_rounding ? "Rounding Enabled" : "Rounding Disabled"}
                                                        >
                                                            <RotateCcw size={14} className="me-1" />
                                                            {formData.enable_rounding ? 'Rounded' : 'Actual'}
                                                        </button>
                                                        <h3 className="mb-0 text-white">
                                                            {formatCurrency(currentGrandTotal)}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="card bg-light">
                                            <div className="card-body py-2">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">Discount</span>
                                                    <strong className="text-danger">{formatCurrency(formData.discount)}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Section */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Courier / Shipping</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={getRaw('shipping', formData.shipping)}
                                            onChange={(e) => onNumChange('shipping', e.target.value, (v) => handleShippingChange(v))}
                                            onFocus={() => onNumFocus('shipping', formData.shipping)}
                                            onBlur={() => onNumBlur('shipping')}
                                            min="0"
                                            step="any"
                                            disabled={formData.due_amount <= 0}
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Additional Charge</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={getRaw('additional_charge', formData.additional_charge || 0)}
                                            onChange={(e) => onNumChange('additional_charge', e.target.value, (v) => handleAdditionalChargeChange(v))}
                                            onFocus={() => onNumFocus('additional_charge', formData.additional_charge || 0)}
                                            onBlur={() => onNumBlur('additional_charge')}
                                            min="0"
                                            step="any"
                                            disabled={formData.due_amount <= 0}
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Paid Amount</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">₹</span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={getRaw('paid_amount', formData.paid_amount)}
                                                onChange={(e) => onNumChange('paid_amount', e.target.value, (v) => handlePaidAmountChange(v))}
                                                onFocus={() => onNumFocus('paid_amount', formData.paid_amount)}
                                                onBlur={() => onNumBlur('paid_amount')}
                                                min="0"
                                                max={currentGrandTotal}
                                                step="any"
                                                disabled={formData.due_amount <= 0}
                                            />
                                        </div>
                                        <div className="d-flex justify-content-between mt-1">
                                            <small className="text-muted">
                                                From history: {formatCurrency(totalPaidFromHistory)}
                                            </small>
                                            {formData.due_amount > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-success"
                                                    onClick={handleFullPayment}
                                                >
                                                    Pay Full
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Due Amount</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">₹</span>
                                            <input
                                                type="text"
                                                className={`form-control bg-light ${formData.due_amount > 0 ? 'text-danger' : 'text-success'} fw-bold`}
                                                value={formatCurrency(formData.due_amount)}
                                                readOnly
                                            />
                                        </div>
                                        <small className="text-muted">
                                            {formData.due_amount > 0
                                                ? `Remaining to pay: ${formatCurrency(formData.due_amount)}`
                                                : '✓ Fully paid'}
                                        </small>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Payment Mode</label>
                                        <Select
                                            options={paymentModeOptions}
                                            value={paymentModeOptions.find(opt => opt.value === formData.payment_mode)}
                                            onChange={handlePaymentModeChange}
                                            placeholder="Select mode"
                                            isClearable
                                            isDisabled={formData.due_amount <= 0}
                                        />
                                        {formData.payment_mode && (
                                            <small className="text-muted d-block mt-1">
                                                Current: {paymentModeOptions.find(opt => opt.value === formData.payment_mode)?.label}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                {/* Payment History Display */}
                                {formData.payment_history && Array.isArray(formData.payment_history) && formData.payment_history.length > 0 ? (
                                    <div className="row g-3 mb-3">
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header bg-light py-2">
                                                    <h6 className="mb-0">
                                                        <Clock size={14} className="me-2" />
                                                        Payment History ({formData.payment_history.length})
                                                    </h6>
                                                </div>
                                                <div className="card-body p-2">
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-bordered mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Amount</th>
                                                                    <th>Method</th>
                                                                    <th>Notes</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {formData.payment_history.map((payment, idx) => (
                                                                    <tr key={payment.id || idx}>
                                                                        <td>{(payment.date || payment.payment_date) ? formatDate(payment.date || payment.payment_date) : formatDate(formData.sale_date)}</td>
                                                                        <td className="text-success fw-bold">{formatCurrency(payment.amount)}</td>
                                                                        <td>
                                                                            {(payment.method || payment.payment_method || formData.payment_mode)
                                                                                ? <span className="badge bg-info text-uppercase">{payment.method || payment.payment_method || formData.payment_mode}</span>
                                                                                : <span className="text-muted">-</span>}
                                                                        </td>
                                                                        <td>{payment.notes || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                                <tr className="table-info">
                                                                    <td colSpan="1" className="fw-bold">Total</td>
                                                                    <td className="fw-bold">{formatCurrency(totalPaidFromHistory)}</td>
                                                                    <td colSpan="2"></td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="row g-3 mb-3">
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-header bg-light py-2">
                                                    <h6 className="mb-0">
                                                        <Clock size={14} className="me-2" />
                                                        Payment History
                                                    </h6>
                                                </div>
                                                <div className="card-body p-2 text-center text-muted">
                                                    No payment records found
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional Payment Option - Visible when due amount > 0 */}
                                {formData.due_amount > 0 && (
                                    <div className="row g-3 mb-3">
                                        <div className="col-12">
                                            <div className="card border-success">
                                                <div className="card-body py-2">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="mb-1 text-success">Quick Payment</h6>
                                                            <small>Add payment to clear due amount</small>
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-success"
                                                                onClick={() => {
                                                                    const halfAmount = formData.paid_amount + (formData.due_amount / 2);
                                                                    handlePaidAmountChange(halfAmount);
                                                                }}
                                                            >
                                                                Pay 50% (₹{(formData.due_amount / 2).toFixed(2)})
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                                onClick={handleFullPayment}
                                                            >
                                                                Pay Full (₹{formData.due_amount.toFixed(2)})
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Status and Remarks */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Status</label>
                                        <Select
                                            options={statusOptions}
                                            value={statusOptions.find(s => s.value === formData.status)}
                                            onChange={handleStatusChange}
                                            placeholder="Select status"
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">Payment Status</label>
                                        <Select
                                            options={paymentStatusOptions}
                                            value={paymentStatusOptions.find(opt => opt.value === formData.payment_status)}
                                            onChange={handlePaymentStatusChange}
                                            placeholder="Select status"
                                            isClearable
                                            isDisabled={formData.due_amount <= 0}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Remarks</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.remarks}
                                            onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                            placeholder="Enter remarks"
                                        />
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="text-end">
                                    <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || !hasChanges()}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} className="me-2" />
                                                Update Sale
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Payment Modal */}
            {showPaymentModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Payment</h5>
                                <button type="button" className="btn-close" onClick={() => setShowPaymentModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Additional Amount</label>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={additionalPayment}
                                            onChange={(e) => setAdditionalPayment(parseFloat(e.target.value) || 0)}
                                            min="0.01"
                                            max={formData.due_amount}

                                            autoFocus
                                        />
                                    </div>
                                    <small className="text-muted">
                                        Max: {formatCurrency(formData.due_amount)}
                                    </small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Payment Mode</label>
                                    <Select
                                        options={paymentModeOptions}
                                        value={paymentModeOptions.find(opt => opt.value === newPaymentMode)}
                                        onChange={(opt) => setNewPaymentMode(opt?.value || 'cash')}
                                        placeholder="Select mode"
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary flex-grow-1"
                                        onClick={() => setShowPaymentModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary flex-grow-1"
                                        onClick={handleAdditionalPaymentSubmit}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

EditSale.propTypes = {
    saleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool
};

export default EditSale;